import { createSlice } from "@reduxjs/toolkit";
import {
	fetchCustomerAddresses, updateCustomerAddress, deleteCustomerAddress, fetchCustomerOrders,
	updateCustomerInfo, createCustomerAddress, updateCustomerDefaultAddress, getCustomerProfile,
	influenceDigest
} from "actions/account/profile";

import { CUSTOMER_STATE } from "utils/constants";
import { LOGOUT } from "utils/action-types";

const initialState = {
	displayName: "",
	firstName: "",
	lastName: "",
	emailAddress: {},
	phoneNumber: {},
	creationDate: "",
	addresses: {},
	addressPageInfo: {},
	defaultAddress: null,
	id: "",
	digest: null,
	customerState: null,
	isAuthenticated: false,
	hasOrders: false,
	orders: {
		loading: true,
		data: [],
		pageInfo: {
			admin: null,
			storefront: null,
		},
	},
	error: null,
	disabled: false,
	latestSubOrder: null,
};

const profileSlice = createSlice({
	name: "auth",
	initialState,
	extraReducers: (builder) => {
		builder
			.addCase(LOGOUT, () => initialState)
			.addCase(getCustomerProfile.fulfilled, (state, action) => {
				Object.assign(state, action.payload);
				state.customerState = action.payload.id ? CUSTOMER_STATE.Logged_in : CUSTOMER_STATE.Logged_out;
				state.isAuthenticated = !!action.payload.id;
			})
			.addCase(getCustomerProfile.rejected, (state) => {
				state.customerState = CUSTOMER_STATE.Logged_out;
				state.isAuthenticated = false;
			})
			.addCase(influenceDigest.fulfilled, (state, action) => {
				state.digest = action.payload;
			})
			.addCase(fetchCustomerAddresses.fulfilled, (state, action) => {
				const { addresses, pageInfo, defaultAddress } = action.payload;
				const cursor = action.meta.arg;
				if (cursor) {
					Object.assign(state.addresses, addresses);
				} else {
					state.addresses = addresses;
				}
				state.addressPageInfo = pageInfo;
				state.defaultAddress = defaultAddress;
			})
			.addCase(updateCustomerDefaultAddress.fulfilled, (state, action) => {
				state.defaultAddress = action.payload.address;
			})
			.addCase(updateCustomerAddress.fulfilled, (state, action) => {
				const { address, id } = action.payload;
				if (action.meta.arg.defaultAddress) {
					state.defaultAddress = address;
				}
				state.addresses[id] = address;
			})
			.addCase(createCustomerAddress.fulfilled, (state, action) => {
				const { address, id } = action.payload;
				state.addresses[id] = address;
				if (action.meta.arg.defaultAddress) {
					state.defaultAddress = address;
				}
				state.addresses[id] = address;
			})
			.addCase(deleteCustomerAddress.fulfilled, (state, action) => {
				const { id } = action.payload;
				delete state.addresses[id];
				if (Object.keys(state.addresses).length === 0) {
					state.defaultAddress = null;
				}
			})
			.addCase(updateCustomerInfo.fulfilled, (state, action) => {
				Object.assign(state, action.payload.customer);
			})
			.addCase(fetchCustomerOrders.fulfilled, (state, action) => {
				const arg = action.meta.arg;
				if (arg && arg.admin?.endCursor) {
					state.orders.data = state.orders.data.concat(action.payload.orders);
				} else {
					state.orders.data = action.payload.orders;
				}
				state.orders.pageInfo = action.payload.pageInfo;
				state.orders.loading = false;

				const latestSubOrder = state.orders.data.find((o) => o.isSubscription);
				state.latestSubOrder = latestSubOrder
					? {
						fulfillments: latestSubOrder.fulfillments,
						createdAt: latestSubOrder.createdAt,
						deliveryDate: latestSubOrder.customAttributes?.find(
							(a) => a.key === "Lieferdatum"
						)?.value,
						totalQuantity:
							latestSubOrder.lineItems?.nodes?.reduce(
								(acc, item) => acc + item.quantity,
								0
							) ?? 0,
					}
					: null;
			})
			.addCase(fetchCustomerOrders.rejected, (state) => {
				state.orders.loading = false;
			});
	}
});

export default profileSlice.reducer;
